import React from "react";
import { Building2, User, UserCheck, Package, Calendar, DollarSign, AlertTriangle, MessageCircle, CheckSquare, Sparkles, StickyNote } from "lucide-react";
import { resolveIcon } from "../lib/iconResolver";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";

function fmt(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

// Compact format: no "R$", no decimals — e.g. 3000 → "3.000"
function fmtCompact(v) {
  if (!v && v !== 0) return "0";
  return Math.round(v).toLocaleString("pt-BR");
}

function parseBRNumber(s) {
  if (typeof s === "number") return s;
  if (!s) return 0;
  return parseFloat(String(s).replace(/\./g, "").replace(",", ".")) || 0;
}

const DEFAULT_CARD_FIELDS = [
  { field_name: "titulo", label: "Título", visible: true, bold: true, badge: false },
  { field_name: "empresa_nome", label: "Empresa", visible: true, bold: false, badge: false },
  { field_name: "valor_mrr", label: "MRR", visible: true, bold: false, badge: false },
  { field_name: "valor_setup", label: "Setup", visible: true, bold: false, badge: false },
];

function fmtBRDate(val) {
  if (!val) return null;
  try { return new Date(val).toLocaleDateString("pt-BR"); } catch { return String(val); }
}
function fmtBRDateTime(val) {
  if (!val) return null;
  try { return new Date(val).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return String(val); }
}

function getCardFieldValue(negocio, fieldName, customFields) {
  switch (fieldName) {
    case "titulo": return negocio.titulo;
    case "responsavel": case "responsavel_nome": return negocio.responsavel_nome || null;
    case "empresa": case "empresa_nome": return negocio.empresa_nome || null;
    case "contato": case "contato_nome": return negocio.contato_nome || null;
    case "produto": case "produto_nome": return negocio.produto_nome || null;
    case "mrr": case "valor_mrr": return negocio.valor_mrr ? `MRR ${fmt(negocio.valor_mrr)}` : null;
    case "setup": case "valor_setup": return (negocio.valor_setup > 0) ? `Setup ${fmt(negocio.valor_setup)}` : null;
    case "data_previsao": return null;
    default: {
      const cf = customFields.find(c => c.field_name === fieldName);
      if (!cf) return null;
      const val = negocio.custom_fields?.[fieldName];
      if (val === null || val === undefined || val === "") return null;
      if (cf.field_type === "boolean") return val === true || val === "true" ? "Sim" : null;
      if (cf.field_type === "date") return fmtBRDate(val);
      if (cf.field_type === "datetime") return fmtBRDateTime(val);
      if (cf.field_type === "select" && Array.isArray(val)) return val.join(", ");
      if (cf.field_type === "currency") return `${cf.currency_symbol || "R$"} ${val}`;
      if (cf.field_type === "number") return String(val);
      return String(val);
    }
  }
}

const isFinancial = (f) =>
  ["valor_mrr", "valor_setup", "mrr", "setup"].includes(f.field_name) ||
  f.mrr_field === true || f.setup_field === true ||
  ["mrr", "setup"].includes((f.label || f.field_label || "").toLowerCase());

const KanbanCard = React.memo(function KanbanCard({
  negocio, cardFields, customFields, canDrag, slaBreached, slaAlerts = [],
  whatsappUnread = 0,
  taskCount = null, // { pending: N, overdue: N }
  noteCount = 0,
  onClick, tc500 = "#3B82F6"
}) {
  const allCardFields = cardFields?.length > 0 ? cardFields : DEFAULT_CARD_FIELDS;
  const titleField = allCardFields.find(f => f.field_name === "titulo");
  const allVisible = allCardFields.filter(f => f.visible && f.field_name !== "titulo");
  const financialFields = allVisible.filter(isFinancial);
  const regularFields = allVisible.filter(f => !isFinancial(f));

  const getMrrVal = () => {
    if (negocio.valor_mrr > 0) return negocio.valor_mrr;
    const mrrCF = allCardFields.find(f => f.mrr_field || f.field_name === "mrr" || (f.label || "").toLowerCase() === "mrr");
    return mrrCF ? parseBRNumber(negocio.custom_fields?.[mrrCF.field_name]) : 0;
  };
  const getSetupVal = () => {
    if (negocio.valor_setup > 0) return negocio.valor_setup;
    const setupCF = allCardFields.find(f => f.setup_field || f.field_name === "setup" || (f.label || "").toLowerCase() === "setup");
    return setupCF ? parseBRNumber(negocio.custom_fields?.[setupCF.field_name]) : 0;
  };

  // Determine border style based on status and SLA
  const borderClass = negocio.status === "ganho"  ? "border-2 border-solid border-emerald-500" :
                      negocio.status === "perdido" ? "border-2 border-solid border-rose-500" :
                      slaBreached                  ? "border-2 border-dashed border-amber-300"
                                                   : "border-2 border-slate-200";
  const borderStyle = undefined;
  const TitleIcon = titleField?.icon ? resolveIcon(titleField.icon) : null;

  return (
    <div
      draggable={canDrag}
      onDragStart={canDrag ? (e) => e.dataTransfer.setData("negocio_id", negocio.id) : undefined}
      className={`bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${borderClass}`}
      style={borderStyle}
      onClick={onClick}
    >
      {/* Title */}
      <div className="flex items-center justify-between mb-1.5 min-w-0">
        {titleField?.badge ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs flex-1 mr-1 min-w-0 overflow-hidden ${titleField.bold ? "font-semibold" : "font-medium"}`}
            style={{ backgroundColor: titleField.badge_color || "#3B82F6", color: "#fff" }}>
            {TitleIcon && <TitleIcon className="w-3 h-3 mr-1 flex-shrink-0" />}
            <span className="truncate">{negocio.titulo}</span>
          </span>
        ) : (
          <p className={`text-sm leading-tight flex-1 pr-1 flex items-center gap-1 min-w-0 overflow-hidden ${titleField?.bold ? "font-semibold text-slate-900" : "font-medium text-slate-800"}`}>
            {TitleIcon && <TitleIcon className="w-3 h-3 flex-shrink-0 text-slate-400" />}
            <span className="truncate">{negocio.titulo}</span>
          </p>
        )}
      </div>

      {/* Regular fields */}
      {regularFields.length > 0 && (
        <div className="space-y-1">
          {regularFields.map(f => {
            const value = getCardFieldValue(negocio, f.field_name, customFields);
            if (!value) return null;
            // Configured icon takes priority; fall back to sensible defaults per field type
            const configuredIcon = f.icon ? resolveIcon(f.icon) : null;
            const FieldIcon = configuredIcon || (
              (f.field_name === "empresa" || f.field_name === "empresa_nome") ? Building2 :
              (f.field_name === "contato" || f.field_name === "contato_nome") ? User :
              (f.field_name === "produto" || f.field_name === "produto_nome") ? Package :
              (f.field_name === "responsavel" || f.field_name === "responsavel_nome") ? UserCheck :
              f.field_name === "data_previsao" ? Calendar : null
            );
            if (f.badge) {
              return <span key={f.field_name} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs mr-1 ${f.bold ? "font-semibold" : "font-medium"}`} style={{ backgroundColor: f.badge_color || "#3B82F6", color: "#fff" }}>{value}</span>;
            }
            return (
              <Tooltip key={f.field_name}>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 cursor-default">
                    {FieldIcon && <FieldIcon className="w-3 h-3 flex-shrink-0 text-slate-400" />}
                    <span className={`text-xs text-slate-500 ${f.bold ? "font-semibold" : ""}`}>{value}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  <span className="font-medium">{f.label || f.field_label || f.field_name}:</span> {value}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      )}

      {/* Financial fields — always inline, compact format */}
      {financialFields.length > 0 && (
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {[...financialFields].sort((a, b) => {
            const aSetup = (a.label || "").toLowerCase() === "setup" || a.field_name === "valor_setup" || a.field_name === "setup" || a.setup_field;
            const bSetup = (b.label || "").toLowerCase() === "setup" || b.field_name === "valor_setup" || b.field_name === "setup" || b.setup_field;
            return aSetup ? -1 : bSetup ? 1 : 0;
          }).map(f => {
            const label = f.label || f.field_label || (["valor_mrr","mrr"].includes(f.field_name) ? "MRR" : ["valor_setup","setup"].includes(f.field_name) ? "Setup" : f.field_name);
            // Get numeric value — access raw custom_field value directly to avoid currency prefix issues
            const numericValue = ["valor_mrr","mrr"].includes(f.field_name) ? getMrrVal() :
              ["valor_setup","setup"].includes(f.field_name) ? getSetupVal() :
              (() => {
                const raw = negocio.custom_fields?.[f.field_name];
                if (raw === null || raw === undefined || raw === "") return 0;
                if (typeof raw === "number") return raw;
                return parseBRNumber(String(raw)) || 0;
              })();
            if (!numericValue || numericValue <= 0) return null;
            const FIcon = (f.icon ? resolveIcon(f.icon) : null) || DollarSign;
            return (
              <Tooltip key={f.field_name}>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 flex-shrink-0 cursor-default">
                    <FIcon className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className={`text-xs text-slate-500 ${f.bold ? "font-semibold" : ""}`}>{fmtCompact(numericValue)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent><span className="font-medium">{label}:</span> {fmt(numericValue)}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      )}
      {/* Footer indicators */}
      {(() => {
        const hasWhatsapp = whatsappUnread > 0;
        const hasTask = taskCount && (taskCount.overdue > 0 || taskCount.pending > 0);
        const hasSla = slaBreached && slaAlerts.length > 0;
        const hasAi = !!negocio.ai_summary;
        const hasNotes = noteCount > 0;
        if (!hasWhatsapp && !hasTask && !hasSla && !hasAi && !hasNotes) return null;
        const taskTotal = hasTask ? (taskCount.pending || 0) + (taskCount.overdue || 0) : 0;
        const taskIsOverdue = taskCount?.overdue > 0;
        return (
          <div className="flex items-center gap-1.5 justify-end mt-1.5 pt-1.5 border-t border-slate-100">
            {hasSla && slaAlerts.map((msg, i) => (
              <Tooltip key={`sla-${i}`}>
                <TooltipTrigger asChild>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center cursor-default bg-amber-100">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">{msg}</TooltipContent>
              </Tooltip>
            ))}
            {hasAi && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center cursor-default bg-violet-100">
                    <Sparkles className="w-3 h-3 text-violet-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Análise com IA realizada</TooltipContent>
              </Tooltip>
            )}
            {hasNotes && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center cursor-default bg-blue-100">
                    <StickyNote className="w-3 h-3 text-blue-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">{noteCount} anotaç{noteCount === 1 ? "ão" : "ões"}</TooltipContent>
              </Tooltip>
            )}
            {hasTask && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative w-5 h-5 flex items-center justify-center cursor-default">
                    <span className={`absolute inset-0 rounded-full animate-ping opacity-40 ${taskIsOverdue ? "bg-red-400" : "bg-amber-400"}`} />
                    <div className={`relative w-5 h-5 rounded-full flex items-center justify-center ${taskIsOverdue ? "bg-red-500" : "bg-amber-500"}`}>
                      <CheckSquare className="w-3 h-3 text-white" />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  {taskIsOverdue
                    ? `${taskCount.overdue} tarefa${taskCount.overdue !== 1 ? "s" : ""} vencida${taskCount.overdue !== 1 ? "s" : ""}`
                    : `${taskTotal} tarefa${taskTotal !== 1 ? "s" : ""} pendente${taskTotal !== 1 ? "s" : ""}`}
                </TooltipContent>
              </Tooltip>
            )}
            {hasWhatsapp && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative w-5 h-5 flex items-center justify-center cursor-default">
                    <span className="absolute inset-0 rounded-full animate-ping opacity-40 bg-green-400" />
                    <div data-testid="whatsapp-unread-badge" className="relative w-5 h-5 rounded-full flex items-center justify-center bg-green-500">
                      <MessageCircle className="w-3 h-3 text-white" />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  {whatsappUnread} mensagem{whatsappUnread !== 1 ? "s" : ""} não lida{whatsappUnread !== 1 ? "s" : ""} no WhatsApp
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      })()}
    </div>
  );
});

export default KanbanCard;